import { AttendanceGraphCard, SessionDataPoint } from "@/components/academician/attendance-graph-card"
import { CurrentModuleCard } from "@/components/academician/current-module-card"
import { StartupSummaryCard } from "@/components/academician/startup-summary-card"
import { UpcomingDeadlinesCard } from "@/components/academician/upcoming-deadlines-card"
import { AcademicianButton } from "@/components/academician/academician-button"
import { PlayIcon, CalendarIcon, FileTextIcon, CheckCircleIcon, ClockIcon } from "lucide-react"
import { cn } from "@/lib/utils"

interface CurrentModuleData {
  module_name: string
  progress: number
  current_chapter: string | null
  next_chapter: string | null
  continue_href: string
}

interface LearningProgress {
  videos_completed: number
  videos_total: number
  videos_percentage: number
  quizzes_passed: number
  quizzes_total: number
  quizzes_percentage: number
}

interface ValidationStats {
  interviews_this_week: number
  interviews_total: number
  pain_points_validated: number
  pain_points_invalidated: number
  pain_points_in_progress: number
  customer_segments_tested: number
  validation_confidence: number
}

interface CohortHeader {
  current_week: number | null
  duration_weeks: number | null
  start_date: string | null
  end_date: string | null
}

interface QuickActions {
  continue_learning: { url: string; title: string }
}

interface ActivityItem {
  action: string
  title: string
  timeAgo: string
}

interface UpcomingSession {
  title: string
  date: string
  time: string
  week: number
  format: string
  id: number
}

interface PendingDeadline {
  title: string
  deadline: string
  submitted: boolean
  session_id: number
}

interface BrainstormingStats {
  sessions_attended: number
  sessions_total: number
  assignments_submitted: number
  assignments_total: number
  upcoming_session: UpcomingSession | null
  pending_deadlines: PendingDeadline[]
}

interface TrackingGraphs {
  session_attendance: SessionDataPoint[]
  assignment_submissions: SessionDataPoint[]
  interview_completions: SessionDataPoint[]
  weekly_lead_efforts: SessionDataPoint[]
}

interface DashboardPageProps {
  userName?: string
  cohortName?: string
  cohortStatus?: "active" | "starting-soon" | "completed"
  currentModule?: CurrentModuleData | null
  learningProgress?: LearningProgress | null
  validationStats?: ValidationStats | null
  cohortHeader?: CohortHeader | null
  quickActions?: QuickActions | null
  recentActivity?: ActivityItem[] | null
  brainstormingStats?: BrainstormingStats | null
  trackingGraphs?: TrackingGraphs | null
  className?: string
}

const statusConfig = {
  active: {
    label: "Active",
    color: "bg-primary text-primary-foreground",
  },
  "starting-soon": {
    label: "Starting Soon",
    color: "bg-secondary text-secondary-foreground",
  },
  completed: {
    label: "Completed",
    color: "bg-muted text-muted-foreground",
  },
}

function ProgressBar({
  label,
  completed,
  total,
  percentage,
  colorClass = "bg-primary",
}: {
  label: string
  completed: number
  total: number
  percentage: number
  colorClass?: string
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-semibold text-foreground">
          {completed}/{total}
        </span>
      </div>
      <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
        <div
          className={`h-full ${colorClass} transition-all duration-300 rounded-full`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  )
}

export function DashboardPage({
  userName = "Founder",
  cohortName = "Your Cohort",
  cohortStatus,
  currentModule,
  learningProgress,
  validationStats,
  cohortHeader,
  quickActions,
  recentActivity,
  brainstormingStats,
  trackingGraphs,
  className,
}: DashboardPageProps) {
  const resolvedStatus = cohortStatus || "active"
  const statusStyle = statusConfig[resolvedStatus]

  const continueUrl = quickActions?.continue_learning?.url || "/founder/modules"
  const painPoints = (validationStats?.pain_points_validated || 0) +
    (validationStats?.pain_points_invalidated || 0) +
    (validationStats?.pain_points_in_progress || 0)
  const interviews = validationStats?.interviews_total || 0

  // Build cohort timeline deadlines
  const deadlines = []
  if (cohortHeader?.current_week && cohortHeader?.duration_weeks) {
    const remaining = cohortHeader.duration_weeks - cohortHeader.current_week
    deadlines.push({
      id: "week",
      title: `Week ${cohortHeader.current_week} of ${cohortHeader.duration_weeks}`,
      dueDate: remaining > 0 ? `${remaining} weeks remaining` : "Final week",
      status: remaining <= 1 ? "urgent" as const : "upcoming" as const,
      type: "session" as const,
    })
  }

  // Add upcoming brainstorming session
  if (brainstormingStats?.upcoming_session) {
    const s = brainstormingStats.upcoming_session
    deadlines.push({
      id: `bs-${s.id}`,
      title: s.title,
      dueDate: `${s.date} at ${s.time}`,
      status: "upcoming" as const,
      type: "session" as const,
    })
  }

  // Add pending assignment deadlines
  if (brainstormingStats?.pending_deadlines) {
    brainstormingStats.pending_deadlines.forEach((d) => {
      deadlines.push({
        id: `deadline-${d.session_id}`,
        title: `${d.title}${d.submitted ? " (Submitted)" : ""}`,
        dueDate: d.deadline,
        status: d.submitted ? "upcoming" as const : "urgent" as const,
        type: "submission" as const,
      })
    })
  }

  if (cohortHeader?.end_date) {
    deadlines.push({
      id: "end",
      title: "Cohort Ends",
      dueDate: cohortHeader.end_date,
      status: "upcoming" as const,
      type: "submission" as const,
    })
  }

  // Brainstorming percentages
  const attendancePercentage = brainstormingStats && brainstormingStats.sessions_total > 0
    ? Math.round((brainstormingStats.sessions_attended / brainstormingStats.sessions_total) * 100)
    : 0
  const assignmentPercentage = brainstormingStats && brainstormingStats.assignments_total > 0
    ? Math.round((brainstormingStats.assignments_submitted / brainstormingStats.assignments_total) * 100)
    : 0

  return (
    <div className={cn("bg-background", className)}>
      {/* Header */}
      <header className="bg-card border-b border-border px-8 py-6 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Welcome back, {userName}!
                </h1>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {cohortName}
                  </span>
                  <span
                    className={cn(
                      "text-xs font-medium px-2 py-1 rounded-full",
                      statusStyle.color
                    )}
                  >
                    {statusStyle.label}
                  </span>
                </div>
              </div>
              <a href={continueUrl}>
                <AcademicianButton
                  variant="primary"
                  leftIcon={<PlayIcon />}
                >
                  Continue Learning
                </AcademicianButton>
              </a>
            </div>
          </div>
        </header>

        {/* Tracking Graphs */}
        {trackingGraphs && (
          <div className="px-8 pt-8">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <AttendanceGraphCard
                  title="Session Attendance"
                  data={trackingGraphs.session_attendance || []}
                  color="blue"
                />
                <AttendanceGraphCard
                  title="Assignment Submissions"
                  data={trackingGraphs.assignment_submissions || []}
                  color="amber"
                />
                <AttendanceGraphCard
                  title="Interview Completions"
                  data={trackingGraphs.interview_completions || []}
                  color="green"
                />
                <AttendanceGraphCard
                  title="Weekly Lead Efforts"
                  data={trackingGraphs.weekly_lead_efforts || []}
                  color="purple"
                />
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="px-8 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-8">
                {/* Current Module Card */}
                <CurrentModuleCard
                  moduleName={currentModule?.module_name || "No module assigned"}
                  progress={currentModule?.progress || 0}
                  currentChapter={currentModule?.current_chapter || "Not started"}
                  nextChapter={currentModule?.next_chapter}
                  continueHref={currentModule?.continue_href || "/founder/modules"}
                />

                {/* My Startup Summary Card */}
                <StartupSummaryCard
                  painPointsCount={painPoints}
                  interviewsCount={interviews}
                  viewHref="/founder/startup"
                />
              </div>

              {/* Right Column */}
              <div className="space-y-8">
                {/* Learning Progress */}
                {learningProgress && (
                  <div className="bg-card border border-border rounded-lg p-6 space-y-4">
                    <h3 className="text-lg font-semibold text-foreground">Learning Progress</h3>
                    <div className="space-y-4">
                      <ProgressBar
                        label="Videos Completed"
                        completed={learningProgress.videos_completed}
                        total={learningProgress.videos_total}
                        percentage={learningProgress.videos_percentage}
                        colorClass="bg-primary"
                      />
                      <ProgressBar
                        label="Quizzes Passed"
                        completed={learningProgress.quizzes_passed}
                        total={learningProgress.quizzes_total}
                        percentage={learningProgress.quizzes_percentage}
                        colorClass="bg-green-500"
                      />
                      {brainstormingStats && brainstormingStats.sessions_total > 0 && (
                        <ProgressBar
                          label="Sessions Attended"
                          completed={brainstormingStats.sessions_attended}
                          total={brainstormingStats.sessions_total}
                          percentage={attendancePercentage}
                          colorClass="bg-blue-500"
                        />
                      )}
                      {brainstormingStats && brainstormingStats.assignments_total > 0 && (
                        <ProgressBar
                          label="Assignments Submitted"
                          completed={brainstormingStats.assignments_submitted}
                          total={brainstormingStats.assignments_total}
                          percentage={assignmentPercentage}
                          colorClass="bg-amber-500"
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* Upcoming Deadlines Card */}
                <UpcomingDeadlinesCard deadlines={deadlines.length > 0 ? deadlines : undefined} />
              </div>
            </div>

            {/* Recent Activity */}
            {recentActivity && recentActivity.length > 0 && (
              <div className="mt-8">
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Recent Activity
                  </h3>
                  <div className="space-y-3">
                    {recentActivity.map((activity, index) => {
                      // Choose dot color based on activity type
                      const dotColor = activity.action.includes("Attended")
                        ? "bg-blue-500"
                        : activity.action.includes("Submitted")
                          ? "bg-amber-500"
                          : "bg-primary"

                      return (
                        <div
                          key={index}
                          className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className={`h-2 w-2 rounded-full ${dotColor} mt-2 flex-shrink-0`} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-foreground">
                              <span className="font-medium">{activity.action}</span>
                              {": "}
                              <span className="text-muted-foreground">
                                {activity.title}
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {activity.timeAgo}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
    </div>
  )
}
